import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { LEARN } from "../theme";
import { ConfettiBurst } from "../feedback/Celebration";
import WritingSketchCanvas from "./WritingSketchCanvas";
import { strokeBounds } from "./writingStroke";
import {
  bestLetter,
  evaluatePad,
  evaluateWord,
  makeTargetLayout,
} from "./writingGeometryEvaluator";
import {
  CheckIcon,
  ClearIcon,
  EyeIcon,
  IconButton,
  StickerAction,
  UndoIcon,
  paperSurface,
  withAlpha,
} from "./games/shared";

/**
 * The handwriting surface every Writing Town engine draws on.
 *
 *  - A paper card that fills whatever height the engine gives it (the arena
 *    minus the round chrome), with kindergarten lines aligned to the glyph
 *    frame the evaluator scores against.
 *  - Faded reference glyph with numbered start dots when `showsGuide` (or
 *    when the child peeks).
 *  - Crayon ink; undo-last-stroke and clear; the "Check" sticker.
 *  - Accept: ink settles green + sparkles + sfx("sparkle"), then onAccepted.
 *    Reject: gentle shake, ink blushes coral, onRejected → engine's onWrong.
 *
 * Geometry is judged by evaluatePad / evaluateWord / bestLetter in canvas px
 * (anchored on the guide when tracing, freeform when writing from memory).
 */
const ACCEPT_THRESHOLD = 0.7;

const WritingHandwritingPad = ({
  target,
  accent = LEARN.color.lavender,
  showsGuide = true,
  drawingEnabled = true,
  resetKey = 0,
  submitTitle = "Check",
  mode = "letter",
  letters = null,
  onAccepted,
  onRejected,
  autoSubmit = false,
  statusHint,
  previewLetter = "",
  guideProgress = null,
  showActions = true,
  sfx,
  reduceMotion = false,
  hintAvailable = true,
  hintTitle = "Peek",
  hintForced = false,
  acceptDelay,
  minHeight = 160,
  style,
}) => {
  const [strokes, setStrokes] = useState([]);
  const [hint, setHint] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | accepted | rejected
  const [burst, setBurst] = useState(0);
  const [size, setSize] = useState({ width: 320, height: 220 });
  const paperRef = useRef(null);
  const acceptTimer = useRef(null);
  const rejectTimer = useRef(null);
  const settleTimer = useRef(null);
  const strokesRef = useRef(strokes);
  strokesRef.current = strokes;

  useEffect(() => {
    const node = paperRef.current;
    if (!node) return undefined;
    const update = () => {
      const next = { width: Math.max(1, node.clientWidth), height: Math.max(80, node.clientHeight) };
      setSize((prev) => (prev.width === next.width && prev.height === next.height ? prev : next));
    };
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => () => {
    clearTimeout(acceptTimer.current);
    clearTimeout(rejectTimer.current);
    clearTimeout(settleTimer.current);
  }, []);

  // Reset ink when the target or resetKey changes.
  useEffect(() => {
    setStrokes([]);
    setPhase("idle");
    setHint(false);
    clearTimeout(settleTimer.current);
  }, [target, resetKey, mode]);

  const layout = useMemo(() => makeTargetLayout(target, size), [target, size]);
  const guideVisible = showsGuide || hint || hintForced;
  const placement = showsGuide ? "anchored" : "freeform";

  const guideStrokes = useMemo(
    () =>
      (layout.glyphs || []).flatMap((glyph) => glyph.variants[0]?.strokes || []).map((stroke) => ({
        ...stroke,
        points: (stroke.points || []).map((point) => ({
          x: point.x / Math.max(1, size.width),
          y: point.y / Math.max(1, size.height),
        })),
      })),
    [layout, size.height, size.width],
  );

  const guideLines = useMemo(() => {
    const frame = layout.glyphs?.[0]?.frame;
    if (!frame) return null;
    const h = Math.max(1, size.height);
    return {
      top: (frame.y + frame.height * 0.12) / h,
      mid: (frame.y + frame.height * 0.4) / h,
      base: (frame.y + frame.height * 0.84) / h,
    };
  }, [layout, size.height]);

  const toCanvasInk = useCallback(
    (normalized) =>
      normalized.map((stroke) => ({
        points: stroke.points.map((p) => ({ x: p.x * size.width, y: p.y * size.height })),
      })),
    [size.height, size.width],
  );

  const score = useCallback(
    (ink) => (mode === "word"
      ? evaluateWord(ink, layout, ACCEPT_THRESHOLD, placement)
      : evaluatePad(ink, layout, ACCEPT_THRESHOLD, "easy", placement)),
    [layout, mode, placement],
  );

  const accept = useCallback(
    (acceptedTarget, scored) => {
      setPhase("accepted");
      setBurst((b) => b + 1);
      sfx?.("sparkle", { volume: 0.7 });
      const delay = acceptDelay ?? (reduceMotion ? 120 : 720);
      clearTimeout(acceptTimer.current);
      acceptTimer.current = setTimeout(() => onAccepted?.(acceptedTarget, scored), delay);
    },
    [acceptDelay, onAccepted, reduceMotion, sfx],
  );

  const reject = useCallback(
    (scored) => {
      setPhase("rejected");
      clearTimeout(rejectTimer.current);
      rejectTimer.current = setTimeout(() => setPhase("idle"), reduceMotion ? 200 : 620);
      onRejected?.(scored);
    },
    [onRejected, reduceMotion],
  );

  const recognise = useCallback(
    (nextStrokes, { force = false } = {}) => {
      const ink = toCanvasInk(nextStrokes);
      const match = bestLetter(ink, size, letters || [], ACCEPT_THRESHOLD, "freeform");
      if (match) {
        accept(match.letter, match.result);
        return true;
      }
      if (force) reject(null);
      return false;
    },
    [accept, letters, reject, size, toCanvasInk],
  );

  const handleChange = (nextStrokes) => {
    if (phase === "accepted") return;
    setStrokes(nextStrokes);
    if (phase === "rejected") setPhase("idle");
    if (autoSubmit && letters?.length) {
      // Let the child add a crossbar or a dot before we judge.
      clearTimeout(settleTimer.current);
      settleTimer.current = setTimeout(() => {
        if (strokesRef.current === nextStrokes) recognise(nextStrokes);
      }, 700);
      return;
    }
    if (showsGuide) {
      const scored = score(toCanvasInk(nextStrokes));
      if (scored?.accepted) accept(target, scored);
    }
  };

  const submit = () => {
    if (!strokes.length || phase === "accepted") return;
    clearTimeout(settleTimer.current);
    if (autoSubmit && letters?.length) {
      recognise(strokes, { force: true });
      return;
    }
    const scored = score(toCanvasInk(strokes));
    if (scored?.accepted) accept(target, scored);
    else reject(scored);
  };

  const undo = () => {
    if (!strokes.length || phase === "accepted") return;
    sfx?.("pop", { volume: 0.4 });
    clearTimeout(settleTimer.current);
    setStrokes((prev) => prev.slice(0, -1));
    setPhase("idle");
  };

  const clear = () => {
    if (!strokes.length || phase === "accepted") return;
    sfx?.("whoosh", { volume: 0.4 });
    clearTimeout(settleTimer.current);
    setStrokes([]);
    setPhase("idle");
  };

  const hasInk = strokes.length > 0;
  const inkColor = phase === "accepted" ? LEARN.color.mint : phase === "rejected" ? LEARN.color.coral : accent;
  const canDraw = drawingEnabled && phase !== "accepted";
  const bounds = phase === "accepted" ? strokeBounds(strokes) : null;

  // Only feedback earns a pill on the paper; the idle sheet stays clean.
  const feedback = statusHint || (phase === "accepted" ? "Nice!" : phase === "rejected" ? "Try once more" : "");
  const legend = !feedback && guideVisible && drawingEnabled;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1, minHeight: 0, minWidth: 0, fontFamily: LEARN.font, ...style }}>
      <motion.div
        ref={paperRef}
        animate={phase === "rejected" && !reduceMotion ? { x: [0, -9, 9, -6, 6, -3, 3, 0] } : { x: 0 }}
        transition={{ duration: 0.48, ease: "easeInOut" }}
        style={{
          position: "relative",
          flex: 1,
          minHeight,
          ...paperSurface({ border: phase === "accepted" ? LEARN.color.mint : phase === "rejected" ? withAlpha(LEARN.color.coral, 0.6) : LEARN.color.line }),
          overflow: "hidden",
          transition: "border-color 200ms",
        }}
      >
        {previewLetter ? (
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: Math.min(size.width, size.height) * 0.62,
              lineHeight: 1,
              color: accent,
              opacity: 0.12,
              pointerEvents: "none",
            }}
          >
            {previewLetter}
          </div>
        ) : null}
        <WritingSketchCanvas
          key={`${target}-${resetKey}`}
          strokes={strokes}
          onChange={handleChange}
          width={size.width}
          height={size.height}
          disabled={!canDraw}
          guideStrokes={guideStrokes}
          guideLines={guideLines}
          accent={accent}
          inkColor={inkColor}
          showGuide={guideVisible}
          showStartDots={guideVisible}
          guideProgress={guideProgress}
        />
        {phase === "accepted" && (
          <ConfettiBurst
            key={burst}
            count={18}
            spread={70}
            originX={(bounds?.midX ?? 0.5) * 100}
            originY={(bounds?.midY ?? 0.5) * 100 - 10}
            reduceMotion={reduceMotion}
          />
        )}

        {/* corner tools: small frosted circles that sit quietly on the sheet */}
        {showActions && drawingEnabled && (
          <div style={{ position: "absolute", top: 10, right: 10, display: "flex", gap: 8 }}>
            <IconButton label="Undo last stroke" onClick={undo} disabled={!hasInk || phase === "accepted"} size={40} frosted accent={LEARN.color.ink}>
              <UndoIcon size={18} />
            </IconButton>
            <IconButton label="Clear the paper" onClick={clear} disabled={!hasInk || phase === "accepted"} size={40} frosted accent={LEARN.color.ink}>
              <ClearIcon size={18} />
            </IconButton>
          </div>
        )}
        {showActions && drawingEnabled && !showsGuide && hintAvailable && !hintForced && (
          <div style={{ position: "absolute", top: 10, left: 10 }}>
            <IconButton label={hint ? "Hide the guide" : `${hintTitle}: show the guide`} onClick={() => { setHint((v) => !v); sfx?.("pop", { volume: 0.35 }); }} size={40} frosted={!hint} filled={hint} accent={hint ? "#fff" : accent} style={hint ? { background: accent } : undefined}>
              <EyeIcon size={18} off={hint} />
            </IconButton>
          </div>
        )}

        {/* start-dot legend (guide only) or the accept / retry pill */}
        {(legend || feedback) && (
          <div
            aria-live={feedback ? "polite" : undefined}
            style={{
              position: "absolute",
              left: 12,
              bottom: 10,
              height: 30,
              padding: "0 12px 0 8px",
              borderRadius: 999,
              background: phase === "accepted" ? withAlpha(LEARN.color.mint, 0.16) : "rgba(255,255,255,0.86)",
              border: `1.5px solid ${phase === "accepted" ? LEARN.color.mint : phase === "rejected" ? withAlpha(LEARN.color.coral, 0.5) : "rgba(11,26,51,0.10)"}`,
              fontWeight: 700,
              fontSize: 13,
              color: phase === "accepted" ? LEARN.color.mintDeep : phase === "rejected" ? LEARN.color.coralDeep : LEARN.color.muted,
              display: "flex",
              alignItems: "center",
              gap: 7,
              pointerEvents: "none",
              maxWidth: "calc(100% - 24px)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {legend && (
              <span aria-hidden="true" style={{ width: 18, height: 18, borderRadius: "50%", background: accent, border: "2px solid #fff", boxShadow: `0 0 0 1px ${withAlpha(accent, 0.4)}`, color: "#fff", fontSize: 11, display: "grid", placeItems: "center", lineHeight: 1 }}>1</span>
            )}
            {legend ? "Start at the dot" : feedback}
          </div>
        )}
      </motion.div>

      {showActions && drawingEnabled && (
        <div style={{ display: "flex", gap: 10, justifyContent: "center", alignItems: "center", flexShrink: 0 }}>
          <StickerAction
            onClick={submit}
            accent={accent}
            disabled={!hasInk || phase === "accepted"}
            size="lg"
            icon={<CheckIcon size={22} />}
            ariaLabel={autoSubmit ? "Check this letter" : `${submitTitle} my writing`}
            style={{ minWidth: 200, minHeight: 58 }}
          >
            {submitTitle}
          </StickerAction>
        </div>
      )}
    </div>
  );
};

export default WritingHandwritingPad;
